// Business-timestamp display helper (Phase 37.2.1, timezone made
// configurable in Phase 37.3).
//
// The backend stores every timestamp as naive UTC. This app has no
// date-filter/report surface (confirmed by audit — no dashboard, no date
// pickers, no date_from/date_to ever sent), only two places that DISPLAY a
// stored timestamp (Order History, Order Detail). Both must show
// business-local time (Setup > Company Profile's configured IANA timezone),
// not the customer's own device timezone.
//
// Resolved via GET /api/public-settings — the same public, unauthenticated
// endpoint frontend_Admin's own utils/businessDate.js calls (see
// backend/routers/guest_requests.py) — using the identical endpoint (not a
// second, different one) is what guarantees this app and the Admin app can
// never disagree on which timezone is current. Fetched once, lazily, the
// first time a formatter is actually used; falls back to DEFAULT_TIMEZONE
// ("Asia/Phnom_Penh") until that resolves, and forever after if it fails —
// never throws, never blocks render. Mirrors frontend_Admin's own technique
// exactly (explicit Intl.DateTimeFormat timeZone) rather than a different
// approach — kept as its own small file since frontend_user and
// frontend_Admin are independent CRA builds with no shared source.

import { getPublicSettings } from "../services/settingsService";

export const DEFAULT_TIMEZONE = "Asia/Phnom_Penh";

let _timezone = DEFAULT_TIMEZONE;
let _fetchStarted = false;

function _loadTimezone() {
  if (_fetchStarted) return;
  _fetchStarted = true;
  // Shared API client (same base URL/auth handling as every other call).
  getPublicSettings()
    .then((data) => {
      if (data && data.timezone) _timezone = data.timezone;
    })
    .catch(() => {
      // Network/parse failure: keep DEFAULT_TIMEZONE.
    });
}

function _currentTimezone() {
  _loadTimezone(); // no-ops after the first call
  return _timezone;
}

function parseBackendTimestamp(iso) {
  if (!iso) return null;
  const withZone = iso.includes("Z") || iso.includes("+") ? iso : `${iso}Z`;
  const d = new Date(withZone);
  return Number.isNaN(d.getTime()) ? null : d;
}

const _dateTimeCache = new Map();
function _dateTimeFormatterFor(tz) {
  let f = _dateTimeCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    _dateTimeCache.set(tz, f);
  }
  return f;
}
const _dateCache = new Map();
function _dateFormatterFor(tz) {
  let f = _dateCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "2-digit", month: "short", year: "numeric" });
    _dateCache.set(tz, f);
  }
  return f;
}

export function formatBusinessDateTime(iso) {
  const d = parseBackendTimestamp(iso);
  return d ? _dateTimeFormatterFor(_currentTimezone()).format(d) : (iso || "—");
}

export function formatBusinessDate(iso) {
  const d = parseBackendTimestamp(iso);
  return d ? _dateFormatterFor(_currentTimezone()).format(d) : (iso || "—");
}
